import { X } from 'lucide-react';
import { LogPulseLogo } from './LogPulseLogo';
import { buttonVariants } from './ui/Button';
import { LanguageSelector } from './LanguageSelector';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from './LocalizedLink';
import { cn } from '@/lib/utils';
import { SITE_LOCALE } from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const t = useT();

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-gray-950 z-50 shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <LogPulseLogo className="h-7 w-7" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">LogPulse</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Links */}
          <nav className="flex-1 p-4 space-y-1">
            <LocalizedAnchor
              href="/#features"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {t('nav.features')}
            </LocalizedAnchor>
            <LocalizedAnchor
              href="/mcp"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              MCP
            </LocalizedAnchor>
            <LocalizedAnchor
              href="/#pricing"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {t('nav.pricing')}
            </LocalizedAnchor>
            <a
              href="/docs"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {t('footer.docs')}
            </a>
            <LocalizedAnchor
              href="/guides"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Guides
            </LocalizedAnchor>
            <a
              href="/blog"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Blog
            </a>
            <LocalizedAnchor
              href="/community"
              onClick={onClose}
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Community
            </LocalizedAnchor>
            <a
              href="https://app.logpulse.io/sign-in"
              className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {t('nav.signIn')}
            </a>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-3">
            <LanguageSelector />
            {SITE_LOCALE === 'nl' && (
              <a
                href="https://booking.logpulse.io/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('book_demo_click', { surface: 'mobile_menu' });
                  onClose();
                }}
                className={buttonVariants({ variant: 'outline', size: 'lg', className: 'w-full' })}
              >
                {t('nav.bookDemo')}
              </a>
            )}
            <a href="https://app.logpulse.io/sign-up" className={buttonVariants({ variant: 'gradient', size: 'lg', className: 'w-full' })}>
              {t('nav.getStarted')}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
